import React, { Suspense, lazy } from 'react';
import { Modal, Slide, Box, Typography, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FORM_TOKENS } from './formStyles';

/**
 * Full-screen submission sheet for mobile.
 *
 * Instead of `window.open(..., '_blank')` (which spawned a new tab and threw
 * the user out of the app), the chosen input form is rendered in-place as an
 * overlay that covers the whole viewport EXCEPT the bottom navigation bar, so
 * the user never loses their place. Closing is done with the X in the header.
 *
 * The forms themselves are untouched — they are simply rendered with
 * `embedded` so they drop their standalone page chrome.
 */

const EventInputPage = lazy(() => import('./EventInputPage'));
const ReviewInputPage = lazy(() => import('./ReviewInputPage'));
const CouponInputPage = lazy(() => import('./CouponInputPage'));

const FORM_COMPONENTS = {
  event: EventInputPage,
  review: ReviewInputPage,
  coupon: CouponInputPage
};

const SHEET_TITLES = {
  TH: {
    event: 'เพิ่มอีเว้น/คอนเสิร์ต/กิจกรรม',
    review: 'เพิ่มรีวิวร้านค้า/ร้านอาหาร',
    coupon: 'เพิ่มคูปอง/ส่วนลด',
    close: 'ปิด'
  },
  CN: {
    event: '添加活动/演唱会',
    review: '添加商店/餐厅评论',
    coupon: '添加优惠券/折扣',
    close: '关闭'
  },
  EN: {
    event: 'Add Event / Concert / Activity',
    review: 'Add Shop / Restaurant Review',
    coupon: 'Add Coupon / Discount',
    close: 'Close'
  }
};

const MobileFormSheet = ({ open, type, onClose, language = 'EN' }) => {
  const FormComponent = type ? FORM_COMPONENTS[type] : null;
  const texts = SHEET_TITLES[language] || SHEET_TITLES.EN;
  const isOpen = Boolean(open && FormComponent);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeAfterTransition
      // The bottom nav stays interactive behind the sheet, so we must not
      // let the modal's focus trap fight the user for those taps.
      disableEnforceFocus
      disableAutoFocus
      aria-labelledby="mobile-form-sheet-title"
      slotProps={{
        backdrop: {
          sx: {
            // Stop the scrim above the bottom nav so the nav stays visible.
            bottom: 'var(--mobile-bottom-nav-height)',
            backgroundColor: 'rgba(17, 24, 39, 0.45)'
          }
        }
      }}
    >
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            // Everything except the bottom navigation.
            bottom: 'var(--mobile-bottom-nav-height)',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: FORM_TOKENS.pageBg,
            borderBottomLeftRadius: 18,
            borderBottomRightRadius: 18,
            overflow: 'hidden',
            outline: 'none',
            boxShadow: '0 -10px 40px rgba(17, 24, 39, 0.25)'
          }}
        >
          {/* ---------- Sheet header (sticky, with X on the right) ---------- */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 'calc(var(--safe-area-top) + 10px)',
              pb: 1.25,
              pl: 'max(16px, var(--safe-area-left))',
              pr: 'max(8px, var(--safe-area-right))',
              backgroundColor: 'rgba(255, 255, 255, 0.96)',
              backdropFilter: 'blur(18px)',
              borderBottom: `1px solid ${FORM_TOKENS.border}`,
              zIndex: 1
            }}
          >
            <Typography
              id="mobile-form-sheet-title"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: '1.02rem',
                fontWeight: 700,
                color: FORM_TOKENS.text,
                letterSpacing: '-0.01em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {type ? texts[type] : ''}
            </Typography>
            <IconButton
              onClick={onClose}
              aria-label={texts.close}
              sx={{
                flexShrink: 0,
                width: 38,
                height: 38,
                color: FORM_TOKENS.text,
                backgroundColor: 'rgba(17, 24, 39, 0.05)',
                '&:hover': { backgroundColor: 'rgba(17, 24, 39, 0.1)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* ---------- Scrollable form body ---------- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              px: 1.5,
              pt: 1.5,
              pb: 4
            }}
          >
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} sx={{ color: FORM_TOKENS.brand }} />
                </Box>
              }
            >
              {FormComponent && <FormComponent embedded />}
            </Suspense>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};

export default MobileFormSheet;
